import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { UPDATE_ENTERPRISE_STORE_WEEKDAY_CAPACITIES } from "src/constants/mutations";
import { ENTERPRISE_STORE_WEEKDAY_CAPACITIES } from "src/constants/queries";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { BtnLoader } from "src/Loader";
import { failureToast, successToast } from "src/utils/toasts";

type WeekdayCapacity = {
  dayOfWeek: number;
  capacity: number | null;
  active: boolean;
};

const WEEKDAYS = [
  { dayOfWeek: 1, label: "Monday" },
  { dayOfWeek: 2, label: "Tuesday" },
  { dayOfWeek: 3, label: "Wednesday" },
  { dayOfWeek: 4, label: "Thursday" },
  { dayOfWeek: 5, label: "Friday" },
  { dayOfWeek: 6, label: "Saturday" },
  { dayOfWeek: 7, label: "Sunday" },
];

const emptyRows = (): WeekdayCapacity[] =>
  WEEKDAYS.map((day) => ({
    dayOfWeek: day.dayOfWeek,
    capacity: null,
    active: false,
  }));

const normalizeRows = (rows: WeekdayCapacity[]): WeekdayCapacity[] => {
  const byDay = new Map(rows.map((row) => [row.dayOfWeek, row]));
  return WEEKDAYS.map((day) => {
    const row = byDay.get(day.dayOfWeek);
    return {
      dayOfWeek: day.dayOfWeek,
      capacity: row?.capacity ?? null,
      active: Boolean(row?.active),
    };
  });
};

const EnterpriseCapacitySettings = () => {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const storeCorrelationId = hasRestaurant?.correlationId;
  const [rows, setRows] = useState<WeekdayCapacity[]>(emptyRows());
  const [initialRows, setInitialRows] = useState<WeekdayCapacity[]>(emptyRows());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!storeCorrelationId) return;
    setLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: ENTERPRISE_STORE_WEEKDAY_CAPACITIES,
        variables: { storeCorrelationId },
      });
      const err = data?.errors?.[0];
      if (err) throw new Error(err.message);
      const nextRows = normalizeRows(data?.data?.enterpriseStoreWeekdayCapacities ?? []);
      setRows(nextRows);
      setInitialRows(nextRows);
    } catch (error) {
      failureToast(error instanceof Error ? error.message : "Could not load Enterprise capacity settings");
    } finally {
      setLoading(false);
    }
  }, [axBe, storeCorrelationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const hasChanged = useMemo(
    () => JSON.stringify(rows) !== JSON.stringify(initialRows),
    [rows, initialRows]
  );

  const updateRow = (dayOfWeek: number, patch: Partial<WeekdayCapacity>) => {
    setRows((currentRows) =>
      currentRows.map((row) =>
        row.dayOfWeek === dayOfWeek ? { ...row, ...patch } : row
      )
    );
  };

  const save = async () => {
    if (!storeCorrelationId) return;
    const capacities = rows.map((row) => ({
      dayOfWeek: row.dayOfWeek,
      capacity: row.active ? Math.max(0, Math.floor(Number(row.capacity ?? 0))) : 0,
      active: row.active,
    }));

    setSaving(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_ENTERPRISE_STORE_WEEKDAY_CAPACITIES,
        variables: {
          input: {
            storeCorrelationId,
            capacities,
          },
        },
      });
      const err = data?.errors?.[0];
      if (err) throw new Error(err.message);
      const nextRows = normalizeRows(data?.data?.updateEnterpriseStoreWeekdayCapacities ?? []);
      setRows(nextRows);
      setInitialRows(nextRows);
      successToast("Enterprise capacities saved");
    } catch (error) {
      failureToast(error instanceof Error ? error.message : "Could not save Enterprise capacities");
    } finally {
      setSaving(false);
    }
  };

  if (!storeCorrelationId) {
    return <p className="text-sm text-gray-500">Select a restaurant first.</p>;
  }

  return (
    <div className="w-full max-w-[760px] space-y-4">
      <div className="bg-white rounded-[16px] px-6 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_20px_rgba(0,0,0,0.08)]">
        <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-2">
          Enterprise Capacity
        </h2>
        <p className="text-[14px] text-[#6b7280] mb-6">
          Set the default number of Enterprise meals your restaurant can prepare for each weekday. EatsBits copies the relevant value when a daily Enterprise menu is created.
        </p>

        {loading ? (
          <p className="text-sm text-[#6b7280]">Loading…</p>
        ) : (
          <div className="space-y-3">
            {WEEKDAYS.map((day) => {
              const row = rows.find((entry) => entry.dayOfWeek === day.dayOfWeek);
              const enabled = Boolean(row?.active);
              return (
                <div
                  key={day.dayOfWeek}
                  className="grid grid-cols-1 sm:grid-cols-[1fr_150px_110px] gap-3 sm:items-center rounded-xl border border-[#E5E7EB] bg-white px-4 py-3"
                >
                  <div>
                    <div className="text-sm font-semibold text-[#111827]">{day.label}</div>
                    <div className="text-xs text-[#6b7280]">Default Enterprise meal capacity</div>
                  </div>

                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={row?.capacity ?? ""}
                    disabled={!enabled}
                    onChange={(event) =>
                      updateRow(day.dayOfWeek, {
                        capacity: event.target.value === "" ? null : Number(event.target.value),
                      })
                    }
                    className={`h-10 rounded-xl border px-3 text-sm outline-none transition-colors ${
                      enabled
                        ? "border-[#D1D5DB] bg-white text-[#111827] focus:border-[#FD7657]"
                        : "border-[#E5E7EB] bg-[#F9FAFB] text-[#9CA3AF]"
                    }`}
                  />

                  <label className="flex items-center gap-2 cursor-pointer sm:justify-end">
                    <input
                      type="checkbox"
                      className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] rounded border border-[#9B9B9B] cursor-pointer appearance-none"
                      checked={enabled}
                      onChange={(event) =>
                        updateRow(day.dayOfWeek, {
                          active: event.target.checked,
                          capacity: event.target.checked ? row?.capacity ?? 0 : row?.capacity,
                        })
                      }
                    />
                    <span className="text-sm text-[#374151]">Active</span>
                  </label>
                </div>
              );
            })}

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => void save()}
                disabled={!hasChanged || saving}
                className="inline-flex h-10 items-center justify-center rounded-xl bg-[#FD7657] px-5 text-sm font-semibold text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? <BtnLoader /> : "Save Changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnterpriseCapacitySettings;
